import clsx from "clsx";
import { useLocale } from "@/i18n";
import { formatCurrency, formatGrossFromNet } from "@/lib/money";
import { formatPriceLineCalculation } from "@/lib/priceLineDisplay";
import type { PriceQuote } from "@/types";

type Props = {
  quote: PriceQuote | null;
  loading?: boolean;
  /** Kompakter Stil für die Fußzeile */
  compact?: boolean;
  className?: string;
};

export function PriceBreakdown({ quote, loading, compact, className }: Props) {
  const { messages: m, locale } = useLocale();

  if (!quote) {
    if (!loading) return null;
    if (compact) {
      return (
        <div className={clsx("text-sm", className)} aria-busy="true" aria-live="polite">
          <p className="font-semibold text-slate-500">{m.price.calculating}</p>
          <p className="text-slate-400">&nbsp;</p>
        </div>
      );
    }
    return (
      <div
        className={clsx(
          "animate-pulse rounded-xl border border-slate-200 bg-slate-50 px-4 py-3",
          className,
        )}
        aria-busy="true"
      >
        <p className="text-sm text-slate-500">{m.price.calculating}</p>
      </div>
    );
  }

  const nightLabel = quote.nights === 1 ? m.common.night : m.common.nights;

  if (quote.price_pending) {
    if (compact) {
      return (
        <div className={clsx("text-sm", className)}>
          <p className="font-semibold text-slate-900">{m.price.pending}</p>
          <p className="text-slate-500">
            {quote.nights} {nightLabel}
          </p>
        </div>
      );
    }
    return (
      <aside
        className={clsx("rounded-xl border border-amber-200 bg-amber-50 p-4 md:p-5", className)}
        aria-label={m.price.overview}
      >
        <p className="bk-eyebrow">{m.price.pending}</p>
        <p className="mt-2 text-sm text-slate-700">{m.price.pendingHint}</p>
      </aside>
    );
  }

  if (compact) {
    return (
      <div className={clsx("text-sm", className)} aria-busy={loading || undefined}>
        <p className="font-semibold text-slate-900">
          {m.common.approx} {formatCurrency(quote.total_amount, quote.currency, locale)}
        </p>
        <p className="text-slate-500">
          {quote.nights} {nightLabel} · {m.common.inclVat}
        </p>
      </div>
    );
  }

  return (
    <aside
      className={clsx(
        "rounded-xl border border-brand-100 bg-brand-50/50 p-4 md:p-5",
        className,
      )}
      aria-label={m.price.overview}
      aria-busy={loading || undefined}
    >
      <div className="flex items-baseline justify-between gap-3 border-b border-brand-100 pb-3">
        <div>
          <p className="bk-eyebrow">{m.price.estimatedTotal}</p>
          <p className="mt-1 text-2xl font-bold tabular-nums tracking-tight text-slate-900">
            {formatCurrency(quote.total_amount, quote.currency, locale)}
          </p>
        </div>
        <p className="text-right text-sm text-slate-600">
          {quote.nights} {nightLabel}
          <span className="block text-xs text-slate-500">{m.common.inclVat}</span>
        </p>
      </div>

      {quote.lines.length > 0 ? (
        <ul className="mt-3 space-y-2 text-sm text-slate-700">
          {quote.lines.map((line) => {
            const calculation = formatPriceLineCalculation(line, quote, m, locale);
            return (
              <li
                key={`${line.rule_type}-${line.article_id}`}
                className="flex justify-between gap-3"
              >
                <span className="min-w-0">
                  {line.article_name}
                  {calculation ? (
                    <span className="block text-xs text-slate-500">
                      {calculation}
                      <span className="text-slate-400"> · {m.common.inclVat}</span>
                    </span>
                  ) : null}
                </span>
                <span className="shrink-0 tabular-nums font-medium text-slate-800">
                  {formatCurrency(line.line_total, quote.currency, locale)}
                </span>
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="mt-3 text-sm text-slate-600">
          {quote.nights} ×{" "}
          {formatGrossFromNet(quote.nightly_rate, quote.tax_rate_percent, quote.currency, locale)}{" "}
          {m.common.inclVat}
        </p>
      )}

      <p className="mt-3 text-xs text-slate-500">{m.price.disclaimer}</p>
    </aside>
  );
}
